import { useState, useEffect } from "react";
import { api, dateIT } from "@/lib/api";
import { PageHeader, Btn, Field, Select, Badge, Input, Modal } from "@/components/UI";
import { Robot, Eye, ArrowsClockwise, ChartBar, Sparkle } from "@phosphor-icons/react";

const ACTION_LABEL = {
  extract_visura: "Estrazione Visura",
  extract_investimenti_excel: "Import Excel Investimenti",
  ai_analyze: "Analisi documento",
  ai_apply: "Registrazione documento",
  link_suggest: "Suggerimento collegamento",
  batch_analyze: "Batch analisi",
  apply_batch: "Batch applicato",
  apply_investimenti_bulk: "Bulk investimenti",
};
const ACTION_TONE = {
  extract_visura: "blue", extract_investimenti_excel: "blue",
  ai_analyze: "slate", ai_apply: "green",
  link_suggest: "amber", batch_analyze: "blue",
  apply_batch: "green", apply_investimenti_bulk: "green",
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filiere, setFiliere] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "", filiera_id: "", user_id: "" });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([
        api.get("/ai/audit-log", { params: { ...filters, limit: 200 } }),
        api.get("/ai/audit-log/stats", { params: filters.filiera_id ? { filiera_id: filters.filiera_id } : {} }),
      ]);
      setEntries(e.data);
      setStats(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/filiere").then((r) => setFiliere(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.action, filters.filiera_id, filters.user_id]);

  return (
    <div className="fade-in" data-testid="audit-log-page">
      <PageHeader eyebrow="Amministrazione" title="Audit Log AI"
        actions={<Btn variant="secondary" onClick={load} data-testid="audit-refresh"><ArrowsClockwise size={16} weight="bold" /> Aggiorna</Btn>} />

      <div className="p-8 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="label">Azioni AI totali</div>
              <div className="value">{stats.total}</div>
              <div className="delta">Tracciate</div>
            </div>
            <div className="stat-card md:col-span-2">
              <div className="label">Per tipo azione</div>
              <div className="mt-2 space-y-1.5">
                {stats.by_action.slice(0, 5).map((a) => (
                  <div key={a.action} className="flex items-center justify-between text-sm">
                    <Badge tone={ACTION_TONE[a.action] || "slate"}>{ACTION_LABEL[a.action] || a.action}</Badge>
                    <span className="font-bold text-slate-700">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="stat-card">
              <div className="label">Modelli AI usati</div>
              <div className="mt-2 space-y-1.5">
                {stats.by_model.length === 0 && <div className="text-xs text-slate-400">-</div>}
                {stats.by_model.map((m) => (
                  <div key={m.model} className="flex items-center justify-between text-sm">
                    <span className="mono text-xs">{m.model}</span>
                    <span className="font-bold text-slate-700">{m.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <Field label="Tipo azione">
              <Select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-filter-action">
                <option value="">Tutte</option>
                {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </Field>
          </div>
          <div className="flex-1 min-w-[180px]">
            <Field label="Filiera">
              <Select value={filters.filiera_id} onChange={(e) => setFilters({ ...filters, filiera_id: e.target.value })} data-testid="audit-filter-filiera">
                <option value="">Tutte</option>
                {filiere.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </Select>
            </Field>
          </div>
          {users.length > 0 && (
            <div className="flex-1 min-w-[180px]">
              <Field label="Utente">
                <Select value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} data-testid="audit-filter-user">
                  <option value="">Tutti</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
              </Field>
            </div>
          )}
          <Btn variant="ghost" onClick={() => setFilters({ action: "", filiera_id: "", user_id: "" })}>Reset</Btn>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Caricamento…</div>
          ) : entries.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">Nessuna azione AI registrata</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Timestamp</th><th>Utente</th><th>Azione</th><th>Modello</th>
                  <th>Doc type</th><th>Filiera</th><th>Output</th><th>Decisione</th><th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const filiera = filiere.find((f) => f.id === e.filiera_id);
                  return (
                    <tr key={e.id} data-testid={`audit-row-${e.id}`}>
                      <td className="mono text-xs whitespace-nowrap">{new Date(e.ts).toLocaleString("it-IT")}</td>
                      <td className="text-sm">{e.user_name}</td>
                      <td><Badge tone={ACTION_TONE[e.action] || "slate"}>{ACTION_LABEL[e.action] || e.action}</Badge></td>
                      <td className="mono text-xs">{e.model || "-"}</td>
                      <td className="text-xs">{e.doc_type || "-"}</td>
                      <td className="text-xs">{filiera?.nome || "-"}</td>
                      <td className="text-xs max-w-xs truncate">
                        {e.output_summary?.n_candidates != null && `${e.output_summary.n_candidates} candidati (top ${e.output_summary.top_confidence}%)`}
                        {e.output_summary?.n_created != null && `${e.output_summary.n_created} creati`}
                        {e.output_summary?.n_analyzed != null && `${e.output_summary.n_analyzed} analizzati`}
                        {e.output_summary?.n_proposed != null && `${e.output_summary.n_proposed} proposti`}
                        {e.output_summary?.fields_extracted && `${e.output_summary.fields_extracted.length} campi estratti`}
                        {e.output_summary?.doc_id && `doc:${e.output_summary.doc_id.slice(0, 8)}`}
                      </td>
                      <td>
                        {e.user_decision ? <Badge tone="green">{e.user_decision}</Badge> : <span className="text-slate-400 text-xs">-</span>}
                      </td>
                      <td>
                        <button onClick={() => setDetail(e)} className="w-8 h-8 rounded flex items-center justify-center hover:bg-slate-100" data-testid={`audit-detail-${e.id}`}>
                          <Eye size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Dettaglio azione AI" testId="audit-detail-modal">
        {detail && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="eyebrow">Timestamp</span><div className="mono text-xs mt-1">{new Date(detail.ts).toLocaleString("it-IT")}</div></div>
              <div><span className="eyebrow">Utente</span><div className="mt-1">{detail.user_name}</div></div>
              <div><span className="eyebrow">Azione</span><div className="mt-1"><Badge tone={ACTION_TONE[detail.action] || "slate"}>{ACTION_LABEL[detail.action] || detail.action}</Badge></div></div>
              <div><span className="eyebrow">Modello AI</span><div className="mono text-xs mt-1">{detail.model || "-"}</div></div>
              <div><span className="eyebrow">Tipo doc</span><div className="mt-1">{detail.doc_type || "-"}</div></div>
              <div><span className="eyebrow">Decisione</span><div className="mt-1">{detail.user_decision || "-"}</div></div>
            </div>
            <div>
              <div className="eyebrow mb-1">Input</div>
              <pre className="bg-slate-50 rounded-md p-3 text-xs mono overflow-x-auto">{JSON.stringify(detail.input_summary, null, 2)}</pre>
            </div>
            <div>
              <div className="eyebrow mb-1">Output</div>
              <pre className="bg-slate-50 rounded-md p-3 text-xs mono overflow-x-auto">{JSON.stringify(detail.output_summary, null, 2)}</pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
